import React, { useMemo, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
  StyleSheet,
  FlatList,
  Modal,
  TextInput as RNTextInput,
  Platform,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';
import { Card } from 'react-native-paper';
import FontAwesome from '@react-native-vector-icons/fontawesome';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { RootState } from '../redux/store';
import { useGetPublicPlacesQuery, useGetPublicPlacesSummaryQuery, HKPublicPlace } from '../redux/services/hkpublicplaceApi';
import { useGetEmployeesQuery } from '../redux/services/emmemployeeApi';
import { getContrastColor, ShowToast } from '../Common/common';
import { useInsertPublicCleaningMutation } from '../redux/services/hkpubliccleaningApi';
import Checkbox from '../Components/Checkbox';

import T from '../Components/T';
import { tp } from '../utils/translateText';
const getCategoryIcon = (type: string): string => {
  switch (type) {
    case 'Reception':
      return 'users';
    case 'Restaurant':
      return 'cutlery';
    case 'Bar':
      return 'glass';
    case 'Lobby':
      return 'home';
    case 'Restroom':
      return 'toilets';
    case 'Banquet':
      return 'calendar';
    case 'Gym':
      return 'dumbbell';
    case 'Pool':
      return 'water';
    case 'Corridor':
      return 'arrows-h';
    default:
      return 'building';
  }
};

const getStatusDetails = (status?: string, isOverdue?: number) => {
  if (status === 'I') {
    return {
      label: 'Cleaning In Progress',
      color: '#0288d1',
      bgColor: '#e1f5fe',
    };
  }
  if (status === 'M') {
    return {
      label: 'Under Maintenance',
      color: '#7b1fa2',
      bgColor: '#f3e5f5',
    };
  }
  if (status === 'D' || isOverdue) {
    return {
      label: isOverdue ? 'Overdue / Needs Cleaning' : 'Needs Cleaning',
      color: '#d32f2f',
      bgColor: '#ffebee',
    };
  }
  return {
    label: 'Clean',
    color: '#2e7d32',
    bgColor: '#e8f5e9',
  };
};

interface CleanDialog {
  open: boolean;
  place: HKPublicPlace | null;
}

const PublicPlacesScreen = ({ navigation }: UniversalProps) => {
  const gdata = useSelector((state: RootState) => state.gdata);
  const cid = gdata.company;

  const { data: placesData = [], isLoading } = useGetPublicPlacesQuery(cid, {
    skip: !cid,
  });
  const { data: summaryData } = useGetPublicPlacesSummaryQuery(cid, { skip: !cid });
  const { data: employeeData = [] } = useGetEmployeesQuery(cid);
  const [insertPublicCleaning] = useInsertPublicCleaningMutation();

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [cleanDialog, setCleanDialog] = useState<CleanDialog>({ open: false, place: null });
  const [selectedEmployee, setSelectedEmployee] = useState<number>(0);
  const [targetStatus, setTargetStatus] = useState('C');
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const summary = summaryData || {
    total: 0,
    clean_count: 0,
    dirty_count: 0,
    in_progress_count: 0,
    maintenance_count: 0,
    overdue_count: 0,
  };

  const employees = employeeData?.map((e) => ({ id: e.em_id, label: e.em_name })) || [];

  const categories = ['ALL', 'Reception', 'Restaurant', 'Bar', 'Lobby', 'Restroom', 'Banquet', 'Gym', 'Pool', 'Corridor', 'Other'];

  const places: HKPublicPlace[] = useMemo(() => {
    let filtered = Array.isArray(placesData) ? placesData : [];

    if (categoryFilter !== 'ALL') {
      filtered = filtered.filter((p) => p.pl_type === categoryFilter);
    }

    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.pl_name?.toLowerCase().includes(query) ||
          p.pl_code?.toLowerCase().includes(query) ||
          p.pl_type?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [placesData, categoryFilter, searchTerm]);

  const handleOpenCleanDialog = (place: HKPublicPlace) => {
    setCleanDialog({ open: true, place });
    setSelectedEmployee(0);
    setTargetStatus('C');
    setSelectedTasks([]);
  };

  const handleCloseCleanDialog = () => {
    setCleanDialog({ open: false, place: null });
    setSelectedEmployee(0);
  };

  const handleToggleTask = (task: string) => {
    setSelectedTasks((prev) =>
      prev.includes(task) ? prev.filter((t) => t !== task) : [...prev, task]
    );
  };

  const handleSaveClean = async () => {
    if (!selectedEmployee) {
      ShowToast('Please select an employee');
      return;
    }
    if (!cleanDialog.place) return;

    setIsSaving(true);
    try {
      await insertPublicCleaning({
        cl_company: cid,
        cl_date: moment(gdata.today).format('YYYY/MM/DD HH:mm:ss'),
        cl_place: cleanDialog.place.pl_id,
        cl_statusfrom: cleanDialog.place.pl_status || 'D',
        cl_statusto: targetStatus,
        cl_employee: selectedEmployee,
        cl_narr: selectedTasks.join(', '),
        cl_flag: '1',
      }).unwrap();
      ShowToast('Cleaning logged successfully');
      handleCloseCleanDialog();
    } catch (error) {
      console.error('Error saving cleaning:', error);
      ShowToast('Failed to log cleaning');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color="#2563eb" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}><T>Public Places & Common Areas</T></Text>
          <Text style={styles.subtitle}><T>Cleaning Board</T></Text>
        </View>

        {/* Summary Cards */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.summaryScroll}>
          <TouchableOpacity
            style={[styles.summaryCard, { borderColor: '#2563eb' }]}
            onPress={() => setCategoryFilter('ALL')}
          >
            <Text style={styles.summaryValue}>{summary.total}</Text>
            <Text style={styles.summaryLabel}><T>Total Areas</T></Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.summaryCard, { borderColor: '#2e7d32' }]}
            onPress={() => {}} // Could add status filter later
          >
            <Text style={[styles.summaryValue, { color: '#2e7d32' }]}>{summary.clean_count}</Text>
            <Text style={styles.summaryLabel}><T>Clean</T></Text>
          </TouchableOpacity>

          <TouchableOpacity style={[styles.summaryCard, { borderColor: '#d32f2f' }]}>
            <Text style={[styles.summaryValue, { color: '#d32f2f' }]}>{summary.dirty_count}</Text>
            <Text style={styles.summaryLabel}><T>Needs Cleaning</T></Text>
          </TouchableOpacity>

          <TouchableOpacity style={[styles.summaryCard, { borderColor: '#0288d1' }]}>
            <Text style={[styles.summaryValue, { color: '#0288d1' }]}>{summary.in_progress_count}</Text>
            <Text style={styles.summaryLabel}><T>In Progress</T></Text>
          </TouchableOpacity>

          <TouchableOpacity style={[styles.summaryCard, { borderColor: '#7b1fa2' }]}>
            <Text style={[styles.summaryValue, { color: '#7b1fa2' }]}>{summary.maintenance_count}</Text>
            <Text style={styles.summaryLabel}><T>Maintenance</T></Text>
          </TouchableOpacity>
        </ScrollView>

        {/* Search */}
        <View style={styles.searchContainer}>
          <FontAwesome name="search" size={16} color="#999" style={styles.searchIcon} />
          <RNTextInput
            style={styles.searchInput}
            placeholder={tp("Search area...")}
            placeholderTextColor="#999"
            value={searchTerm}
            onChangeText={setSearchTerm}
          />
        </View>

        {/* Category Filter */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categoryScroll}>
          {categories.map((cat) => (
            <TouchableOpacity
              key={cat}
              style={[
                styles.categoryChip,
                categoryFilter === cat && styles.categoryChipActive,
              ]}
              onPress={() => setCategoryFilter(cat)}
            >
              <Text
                style={[
                  styles.categoryChipText,
                  categoryFilter === cat && styles.categoryChipTextActive,
                ]}
              >
                {cat === 'ALL' ? 'All Areas' : cat}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        {/* Places List */}
        {places.length === 0 ? (
          <View style={styles.emptyContainer}>
            <FontAwesome name="inbox" size={48} color="#ccc" />
            <Text style={styles.emptyText}><T>No public places found</T></Text>
          </View>
        ) : (
          <View style={styles.placesList}>
            {places.map((place) => {
              const status = getStatusDetails(place.pl_status, place.is_overdue);
              const icon = getCategoryIcon(place.pl_type);

              return (
                <Card key={place.pl_id} style={styles.placeCard}>
                  <View style={styles.placeCardHeader}>
                    <View style={styles.placeIconContainer}>
                      <FontAwesome name={icon} size={24} color={status.color} />
                    </View>
                    <View style={styles.placeInfo}>
                      <Text style={styles.placeName}>{place.pl_name}</Text>
                      <Text style={styles.placeType}>
                        {place.pl_floor || place.pl_type} {place.pl_code ? `(${place.pl_code})` : ''}
                      </Text>
                    </View>
                  </View>

                  <View style={styles.placeCardContent}>
                    {/* Status Badge */}
                    <View style={styles.statusRow}>
                      <View style={[styles.statusBadge, { backgroundColor: status.bgColor }]}>
                        <Text style={[styles.statusBadgeText, { color: status.color }]}>
                          {status.label}
                        </Text>
                      </View>
                      {place.is_overdue ? (
                        <View style={styles.overdueBadge}>
                          <FontAwesome name="warning" size={12} color="#fff" />
                          <Text style={styles.overdueText}><T>OVERDUE</T></Text>
                        </View>
                      ) : null}
                    </View>

                    {/* Last Cleaned */}
                    <View style={styles.infoRow}>
                      <FontAwesome name="clock-o" size={14} color="#666" />
                      <View style={styles.infoText}>
                        <Text style={styles.infoLabel}><T>Last Cleaned:</T> </Text>
                        <Text style={styles.infoValue}>
                          {place.pl_last_cleaned_at ? moment(place.pl_last_cleaned_at).fromNow() : 'Never'}
                        </Text>
                      </View>
                    </View>

                    {place.cleaner_name && (
                      <Text style={styles.cleanerName}><T>by</T> {place.cleaner_name}</Text>
                    )}

                    {place.pl_cleaning_freq_hrs && (
                      <Text style={styles.frequencyText}>
                        <T>Target Frequency: Every</T> {place.pl_cleaning_freq_hrs} <T>hours</T>
                      </Text>
                    )}

                    {/* Action Button */}
                    <TouchableOpacity
                      style={[
                        styles.actionButton,
                        {
                          backgroundColor: place.is_overdue ? '#d32f2f' : '#2563eb',
                        },
                      ]}
                      onPress={() => handleOpenCleanDialog(place)}
                    >
                      <FontAwesome name="check-circle" size={16} color="#fff" />
                      <Text style={styles.actionButtonText}><T>Quick Clean / Log</T></Text>
                    </TouchableOpacity>
                  </View>
                </Card>
              );
            })}
          </View>
        )}
      </ScrollView>

      {/* Clean Dialog */}
      <Modal visible={cleanDialog.open} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <FontAwesome name="trash" size={20} color="#2563eb" />
              <Text style={styles.modalTitle}>
                <T>Log Cleaning:</T> {cleanDialog.place?.pl_name}
              </Text>
              <TouchableOpacity onPress={handleCloseCleanDialog}>
                <FontAwesome name="close" size={20} color="#666" />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalBody}>
              {/* Employee Selection */}
              <View style={styles.fieldContainer}>
                <Text style={styles.fieldLabel}><T>Cleaning Attendant / Employee *</T></Text>
                <View style={styles.pickerContainer}>
                  <Picker
                    selectedValue={selectedEmployee}
                    onValueChange={setSelectedEmployee}
                  >
                    <Picker.Item label="Select Employee" value={0} />
                    {employees.map((e) => (
                      <Picker.Item key={String(e.id)} label={e.label} value={e.id} />
                    ))}
                  </Picker>
                </View>
              </View>

              {/* Status Selection */}
              <View style={styles.fieldContainer}>
                <Text style={styles.fieldLabel}><T>New Cleanliness Status</T></Text>
                <View style={styles.pickerContainer}>
                  <Picker
                    selectedValue={targetStatus}
                    onValueChange={setTargetStatus}
                  >
                    <Picker.Item label="Clean (Completed)" value="C" />
                    <Picker.Item label="Cleaning In Progress" value="I" />
                    <Picker.Item label="Under Maintenance" value="M" />
                    <Picker.Item label="Dirty (Needs Attention)" value="D" />
                  </Picker>
                </View>
              </View>

              {/* Task Checklist */}
              <Text style={styles.taskListTitle}>
                <T>Cleaning Tasks Performed (</T>{selectedTasks.length}):
              </Text>

              {[
                'Swept & Mopped Floor',
                'Sanitized High-Touch Surfaces',
                'Emptied Trash & Waste Bins',
                'Restocked Soap & Paper Towels',
                'Disinfected Toilets & Urinals',
                'Wiped Glass & Mirrors',
                'Dusted Furniture & Fixtures',
                'Vacuumed Carpets & Rugs',
              ].map((task) => (
                <TouchableOpacity
                  key={task}
                  style={styles.taskRow}
                  onPress={() => handleToggleTask(task)}
                >
                  <Checkbox
                    value={selectedTasks.includes(task)}
                    onValueChange={() => handleToggleTask(task)}
                  />
                  <Text style={styles.taskLabel}>{task}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            {/* Modal Actions */}
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={[styles.cancelButton, isSaving && { opacity: 0.5 }]}
                onPress={handleCloseCleanDialog}
                disabled={isSaving}
              >
                <Text style={styles.cancelButtonText}><T>Cancel</T></Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.saveButton,
                  (isSaving || !selectedEmployee) && { opacity: 0.5 },
                ]}
                onPress={handleSaveClean}
                disabled={isSaving || !selectedEmployee}
              >
                <Text style={styles.saveButtonText}>
                  {isSaving ? 'Saving...' : 'Save'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  scrollContent: {
    paddingBottom: 80,
  },
  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
  },
  header: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    backgroundColor: '#fff',
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1f2937',
  },
  subtitle: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 2,
  },
  summaryScroll: {
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  summaryCard: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginRight: 12,
    borderWidth: 1.5,
    borderRadius: 8,
    backgroundColor: '#fff',
    minWidth: 100,
    alignItems: 'center',
  },
  summaryValue: {
    fontSize: 18,
    fontWeight: '700',
    color: '#2563eb',
    marginBottom: 4,
  },
  summaryLabel: {
    fontSize: 12,
    color: '#6b7280',
    textAlign: 'center',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    backgroundColor: '#fff',
    marginHorizontal: 12,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    paddingVertical: 8,
    fontSize: 14,
    color: '#1f2937',
  },
  categoryScroll: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginBottom: 12,
  },
  categoryChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 20,
    marginRight: 8,
    backgroundColor: '#fff',
  },
  categoryChipActive: {
    backgroundColor: '#2563eb',
    borderColor: '#2563eb',
  },
  categoryChipText: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
  },
  categoryChipTextActive: {
    color: '#fff',
  },
  placesList: {
    paddingHorizontal: 12,
  },
  placeCard: {
    marginBottom: 12,
    borderRadius: 8,
    overflow: 'hidden',
  },
  placeCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    backgroundColor: '#fafafa',
  },
  placeIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  placeInfo: {
    flex: 1,
  },
  placeName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: 2,
  },
  placeType: {
    fontSize: 12,
    color: '#6b7280',
  },
  placeCardContent: {
    padding: 12,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 4,
  },
  statusBadgeText: {
    fontSize: 11,
    fontWeight: '600',
  },
  overdueBadge: {
    flexDirection: 'row',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    backgroundColor: '#d32f2f',
    alignItems: 'center',
    gap: 4,
  },
  overdueText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#fff',
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 6,
    gap: 8,
  },
  infoText: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  infoLabel: {
    fontSize: 13,
    color: '#6b7280',
  },
  infoValue: {
    fontSize: 13,
    fontWeight: '600',
    color: '#1f2937',
  },
  cleanerName: {
    fontSize: 11,
    color: '#6b7280',
    marginLeft: 20,
    marginBottom: 4,
  },
  frequencyText: {
    fontSize: 11,
    color: '#6b7280',
    marginBottom: 12,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    borderRadius: 6,
    gap: 6,
    marginTop: 8,
  },
  actionButtonText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  emptyContainer: {
    paddingVertical: 48,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
    color: '#999',
    marginTop: 16,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '90%',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    gap: 12,
  },
  modalTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600',
    color: '#1f2937',
  },
  modalBody: {
    padding: 16,
  },
  fieldContainer: {
    marginBottom: 16,
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: 8,
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 6,
    backgroundColor: '#fff',
    overflow: 'hidden',
    height: 50,
  },
  taskListTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: 12,
  },
  taskRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    gap: 12,
  },
  taskLabel: {
    fontSize: 13,
    color: '#374151',
    flex: 1,
  },
  modalActions: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    backgroundColor: '#fff',
    alignItems: 'center',
  },
  cancelButtonText: {
    color: '#374151',
    fontWeight: '600',
    fontSize: 14,
  },
  saveButton: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 6,
    backgroundColor: '#2563eb',
    alignItems: 'center',
  },
  saveButtonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14,
  },
});

export default PublicPlacesScreen;
